import { useState, useMemo } from 'react'
import { Plus, RotateCcw, Search, Layers, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ClientsTable } from '@/components/clients/ClientsTable'
import { ClientSheet } from '@/components/clients/ClientSheet'
import { DeleteClientDialog } from '@/components/clients/DeleteClientDialog'
import { ClientDetailPanel } from '@/components/clients/ClientDetailPanel'
import { ClientContactsSheet } from '@/components/contacts'
import { SavedViewTabs, FilterPill, RecordStatusPill } from '@/components/data-table'
import { useClientsRegistry } from '@/hooks/useClientsRegistry'
import { useRestoreClient } from '@/hooks/useClients'
import { useClientsRegistryStore } from '@/stores/clientsRegistryStore'
import { useTenant } from '@/contexts'
import {
  CLIENT_SAVED_VIEW_CONFIGS,
  CLIENT_GROUPING_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  CLIENT_RELATIONSHIP_OPTIONS,
  CLIENT_CRM_STAGE_OPTIONS,
  CLIENT_TYPE_LABELS,
  CLIENT_CRM_STAGE_LABELS,
  CLIENT_RELATIONSHIP_LABELS,
  getClientDisplayName,
} from '@/types/clients'
import type {
  ClientSavedViewId,
  ClientGroupingOption,
  AccountTypeFilter,
  ClientRelationshipFilter,
  ClientCrmStageFilter,
  ClientRegistryRow,
} from '@/types/clients'

/** Export client data to CSV — slim 9-column format */
function exportClientsCsv(data: ClientRegistryRow[]) {
  const headers = [
    'Client #', 'Name', 'Type', 'Stage', 'Relationship',
    'Primary Contact', '# Aircraft', 'Active Contracts', 'Location',
  ]
  const rows = data.map((o) => [
    o.client_number,
    getClientDisplayName(o),
    CLIENT_TYPE_LABELS[o.client_type],
    CLIENT_CRM_STAGE_LABELS[o.crm_stage],
    CLIENT_RELATIONSHIP_LABELS[o.computed_relationship],
    o.primary_contact_name || '',
    o.aircraft_count.toString(),
    (o.active_lease_count + o.active_transient_count).toString(),
    o.allocation_location_names.join('; '),
  ])

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
}

/**
 * Clients Registry page (v3 — Lean Operational Workbench)
 *
 * Compact 2-row header:
 * - Row 1: Title + Saved View tabs + Add Client button
 * - Row 2: Search + Quick Filter Pills + Grouping + Export
 */
export function ClientsPage() {
  const { currentOrganization } = useTenant()

  const {
    activeView,
    setActiveView,
    quickFilters,
    setAccountTypeFilter,
    setRelationshipFilter,
    setCrmStageFilter,
    resetFiltersToViewDefaults,
    getIsModified,
    search,
    setSearch,
    groupBy,
    setGroupBy,
    selectedClientId,
    setSelectedClientId,
    recordStatus,
    setRecordStatus,
  } = useClientsRegistryStore()

  // Fetch unified data
  const { data, isLoading,  viewCounts } = useClientsRegistry(
    activeView,
    quickFilters,
    recordStatus
  )

  const restoreClientMutation = useRestoreClient()

  const isModified = getIsModified()

  // Find selected client row from data
  const selectedClient = useMemo(
    () => (selectedClientId ? data.find((c) => c.id === selectedClientId) ?? null : null),
    [data, selectedClientId]
  )

  // Sheet/Dialog state
  const [sheetState, setSheetState] = useState<{
    open: boolean
    clientId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [contactsSheetState, setContactsSheetState] = useState<{
    open: boolean
    clientId?: string
  }>({ open: false })

  const handleCreate = () => {
    setSheetState({ open: true, clientId: undefined })
  }

  const handleEdit = (id: string) => {
    setSheetState({ open: true, clientId: id })
  }

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name })
  }

  const handleManageContacts = (clientId: string) => {
    setContactsSheetState({ open: true, clientId })
  }

  const handleContactsSheetOpenChange = (open: boolean) => {
    setContactsSheetState((prev) => ({ ...prev, open }))
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetState((prev) => ({
      open,
      clientId: open ? prev.clientId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleRowClick = (id: string) => {
    setSelectedClientId(id)
  }

  const handleDetailPanelClose = (open: boolean) => {
    if (!open) {
      setSelectedClientId(null)
    }
  }

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Row 1: Title + Saved View Tabs + Add Client */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-baseline gap-2 shrink-0">
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
        </div>

        <div className="flex-1 min-w-0">
          <SavedViewTabs
            views={CLIENT_SAVED_VIEW_CONFIGS}
            activeView={activeView}
            onViewChange={(id) => setActiveView(id as ClientSavedViewId)}
            counts={viewCounts}
            isModified={isModified}
          />
        </div>

        <Button onClick={handleCreate} className="shrink-0" disabled={!currentOrganization}>
          <Plus className="mr-2 h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* Row 2: Search + Quick Filter Pills + Toolbar */}
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, email, #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        <div className="h-4 w-px bg-border mx-1" />

        <FilterPill
          label="Account Type"
          options={ACCOUNT_TYPE_OPTIONS}
          selected={quickFilters.accountType}
          onChange={(v) => setAccountTypeFilter(v as AccountTypeFilter[])}
        />
        <FilterPill
          label="Relationship"
          options={CLIENT_RELATIONSHIP_OPTIONS}
          selected={quickFilters.relationship}
          onChange={(v) => setRelationshipFilter(v as ClientRelationshipFilter[])}
        />
        <FilterPill
          label="Stage"
          options={CLIENT_CRM_STAGE_OPTIONS}
          selected={quickFilters.crmStage}
          onChange={(v) => setCrmStageFilter(v as ClientCrmStageFilter[])}
        />

        <RecordStatusPill value={recordStatus} onChange={setRecordStatus} />

        {isModified && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground hover:text-foreground"
            onClick={resetFiltersToViewDefaults}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset
          </Button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as ClientGroupingOption)}>
            <SelectTrigger className="h-8 w-[140px]">
              <Layers className="mr-2 h-3.5 w-3.5" />
              <SelectValue placeholder="Group by..." />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_GROUPING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => exportClientsCsv(data)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Clients Table */}
      <div className="flex-1 min-h-0">
        <ClientsTable
          data={data}
          isLoading={isLoading}
          activeView={activeView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onManageContacts={handleManageContacts}
          onRestore={(id) => restoreClientMutation.mutate(id)}
          onRowClick={handleRowClick}
        />
      </div>

      {/* Footer Summary Bar */}
      {/* <ClientsFooter summary={summary} activeView={activeView} /> */}

      {/* Client Detail Panel */}
      <ClientDetailPanel
        client={selectedClient}
        clientId={selectedClientId}
        open={!!selectedClientId}
        onOpenChange={handleDetailPanelClose}
        onEdit={handleEdit}
        onManageContacts={handleManageContacts}
      />

      {/* Create/Edit Client Sheet */}
      <ClientSheet
        open={sheetState.open}
        onOpenChange={handleSheetOpenChange}
        clientId={sheetState.clientId}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteClientDialog
          open={!!deleteTarget}
          onOpenChange={handleCloseDeleteDialog}
          clientId={deleteTarget.id}
          clientName={deleteTarget.name}
        />
      )}

      {/* Client Contacts Sheet */}
      {contactsSheetState.clientId && (
        <ClientContactsSheet
          open={contactsSheetState.open}
          onOpenChange={handleContactsSheetOpenChange}
          clientId={contactsSheetState.clientId}
        />
      )}
    </div>
  )
}
